import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useReducedMotion } from '@bliks-io/ui';
import {
  cameraOffset,
  createStillProcess,
  isFinishedTask,
  VIEW_HEIGHT,
  VIEW_WIDTH,
  type ProcessPiece
} from './model';
import { ProcessPieceView } from './ProcessPieceView';
import { useCameraFollow, useEndlessProcess } from './useEndlessProcess';

interface GrowingProcessProps {
  /** Loading is done: finish the process quickly, then call `onFinished`. */
  done?: boolean;
  onFinished?: () => void;
}

interface EndlessProcessLoaderProps extends GrowingProcessProps {
  messageKey: string;
}

const NO_LEAVING_IDS: ReadonlySet<number> = new Set();

function piecesById(pieces: ProcessPiece[]): Map<number, ProcessPiece> {
  return new Map(pieces.map((piece) => [piece.id, piece]));
}

function sourcesOf(piece: ProcessPiece, byId: Map<number, ProcessPiece>): ProcessPiece[] {
  return piece.from.flatMap((id) => byId.get(id) ?? []);
}

const STILL_PROCESS = createStillProcess();
const STILL_PIECES_BY_ID = piecesById(STILL_PROCESS);

function ProcessCanvas({ children }: { children: ReactNode }) {
  return (
    <svg
      width={VIEW_WIDTH}
      height={VIEW_HEIGHT}
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      aria-hidden="true"
      className="max-w-full"
    >
      {children}
    </svg>
  );
}

function StillProcess() {
  return (
    <ProcessCanvas>
      {STILL_PROCESS.map((piece) => (
        <ProcessPieceView
          key={piece.id}
          piece={piece}
          sources={sourcesOf(piece, STILL_PIECES_BY_ID)}
          finished={piece.wave === 1}
          leavingIds={NO_LEAVING_IDS}
          motion="still"
        />
      ))}
    </ProcessCanvas>
  );
}

function GrowingProcess({ done = false, onFinished }: GrowingProcessProps) {
  const { process, firstFastWave } = useEndlessProcess(done, onFinished);
  const cameraRef = useCameraFollow(cameraOffset(process));
  const byId = piecesById(process.pieces);
  const leavingIds = new Set(process.leavingIds);

  return (
    <ProcessCanvas>
      <g ref={cameraRef}>
        {process.pieces.map((piece) => (
          <ProcessPieceView
            key={piece.id}
            piece={piece}
            sources={sourcesOf(piece, byId)}
            finished={isFinishedTask(piece, process)}
            leavingIds={leavingIds}
            motion={piece.wave >= firstFastWave ? 'fast' : 'normal'}
          />
        ))}
      </g>
    </ProcessCanvas>
  );
}

/** Loading screen: a process that keeps growing until loading is done. */
export function EndlessProcessLoader({ messageKey, done, onFinished }: EndlessProcessLoaderProps) {
  const { t } = useTranslation();
  const reducedMotion = useReducedMotion();

  return (
    <div role="status" className="flex h-full flex-col items-center justify-center gap-5">
      {reducedMotion ? <StillProcess /> : <GrowingProcess done={done} onFinished={onFinished} />}
      <p className="text-sm text-muted-foreground">{t(messageKey)}</p>
    </div>
  );
}
